const COOKIEBOT_IDS = {
  EE: '9a2e740d-9929-4e9e-83da-13420793ea4c',
  LV: '72fc07fe-4606-43e2-b090-638c0c480f51',
  LT: '0bb1d40b-c95b-4ca7-b0ec-658d7324ba6d',
} as const;

type Region = keyof typeof COOKIEBOT_IDS;

const getRootDomain = (): string => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  if (parts.length > 2) {
    return parts.slice(-2).join('.');
  }

  return hostname;
};

const setCookie = (
  name: string,
  value: string,
  domain?: string | null,
  path = '/',
  sameSite = 'Lax',
): void => {
  const expiryDate = new Date();
  expiryDate.setFullYear(expiryDate.getFullYear() + 1);

  const domainPart = domain ? `; domain=${domain}` : '';
  const cookieString = `${name}=${value}${domainPart}; path=${path}; expires=${expiryDate.toUTCString()}; SameSite=${sameSite}`;

  document.cookie = cookieString;
};

const getCookieValue = (name: string): string | null => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, ...cookieValueParts] = cookie.trim().split('=');
    if (cookieName === name) {
      return cookieValueParts.join('=');
    }
  }
  return null;
};

const syncHostToRoot = (
  hostCookieName: string,
  rootCookieName: string,
  rootDomain: string,
): void => {
  const hostValue = getCookieValue(hostCookieName);
  if (hostValue) {
    const targetRootDomain = `.${rootDomain}`;
    setCookie(rootCookieName, hostValue, targetRootDomain);
  }
};

const syncRootToHost = (
  hostCookieName: string,
  rootCookieName: string,
): void => {
  const hostValue = getCookieValue(hostCookieName);
  const rootValue = getCookieValue(rootCookieName);

  if (rootValue && (!hostValue || hostValue !== rootValue)) {
    setCookie(hostCookieName, rootValue);
  }
};

const setupCookiebotEventListeners = (rootDomain: string): void => {
  const performCookieSync = (): void => {
    syncHostToRoot('CookieConsent', 'RootCookieConsent', rootDomain);
  };

  window.addEventListener('CookiebotOnConsentReady', () => {
    performCookieSync();
  });

  window.addEventListener('CookiebotOnAccept', () => {
    performCookieSync();
  });

  window.addEventListener('CookiebotOnDecline', () => {
    performCookieSync();
  });
};

export const loadCookiebot = (region: string): void => {
  const validRegion = (
    COOKIEBOT_IDS[region as Region] ? region : 'EE'
  ) as Region;
  const cookiebotId = COOKIEBOT_IDS[validRegion];

  if (document.getElementById('Cookiebot')) {
    return;
  }

  const rootDomain = getRootDomain();

  // Set up Cookiebot config
  (
    window as typeof window & { CookiebotConfig?: { cookieDomain: string } }
  ).CookiebotConfig = {
    cookieDomain: `.${rootDomain}`,
  };

  // Sync root cookie to host BEFORE Cookiebot loads to prevent modal from showing
  // if user already gave consent on another subdomain
  syncRootToHost('CookieConsent', 'RootCookieConsent');

  // Set up event listeners for ongoing sync
  setupCookiebotEventListeners(rootDomain);

  const script = document.createElement('script');
  script.id = 'Cookiebot';
  script.src = 'https://consent.cookiebot.com/uc.js';
  script.setAttribute('data-cbid', cookiebotId);
  script.setAttribute('data-blockingmode', 'auto');
  script.type = 'text/javascript';

  document.head.appendChild(script);
};
